import {
  useMutation,
  useMutationState,
  useQueryClient
} from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../../api";
import { expressOnboardingApi } from "../../api/expressOnboardingApi";
import type { ExpressOnboardingGenerator } from "../../ExpressOnboarding.types";
import { expressOnboardingGeneratorsQueries } from "./useExpressOnboardingGenerators";

type PatchGenerator = Partial<ExpressOnboardingGenerator> & {
  id: ExpressOnboardingGenerator["id"];
};

function useExpressOnboardingMutations() {
  const pendingIds = useMutationState({
    filters: {
      mutationKey: expressOnboardingGeneratorsQueries.list(null).queryKey,
      exact: true,
      status: "pending"
    },
    select: (mutation) => (mutation.state.variables as PatchGenerator).id
  });
  const pendingGeneratorIds = new Set(pendingIds);

  const queryClient = useQueryClient();

  const expressOnboardingGeneratorSave = useMutation({
    mutationFn: async (editedGenerator: PatchGenerator) => {
      const response =
        await apiWithoutCamelization.patch<ExpressOnboardingGenerator>(
          expressOnboardingApi.generator.url(editedGenerator.id),
          editedGenerator
        );
      return response.data;
    },
    mutationKey: expressOnboardingGeneratorsQueries.list(null).queryKey,
    onSuccess: async (generator: PatchGenerator) => {
      await queryClient.invalidateQueries({
        queryKey: expressOnboardingGeneratorsQueries.list(null).queryKey
      });
      queryClient.setQueryData<ExpressOnboardingGenerator[]>(
        expressOnboardingGeneratorsQueries.list(null).queryKey,
        (previous) =>
          previous?.map((g) =>
            g.id === generator.id ? { ...g, ...generator } : g
          )
      );
    }
  });

  return { expressOnboardingGeneratorSave, pendingGeneratorIds };
}

export { useExpressOnboardingMutations };
